import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import { SelectReviewee } from './components/SelectReviewee';
import { SelectRevieweeFormFields, selectRevieweeSchema } from './validation';

import type { Evaluation360FormNavigatorParams } from '@/navigation/Evaluation360/Evaluation360FormNavigator';
import type { UserFilterParams } from '@/types/api';
import type { User } from '@/types/directory/User';
import type { OptionProps } from '@amelio/ui-library';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import {
    useGetUserById,
    useGetUserPermissions,
    useGetCompanyInformation,
    useGetCompanyPeople,
    useGetUsersByIds,
} from '@/api/directory';
import { useUpdateReviewAudience } from '@/api/review-services/review';
import { selectRevieweeOnNextAtom } from '@/atoms/evaluation360';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { Evaluation360Storage } from '@/constants/storage/Evaluation360Storage';
import { useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useCompanyAttributesFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import { useDebounce } from '@/hooks/useDebounce';
import { useError } from '@/hooks/utils/useError';
import { useRegisterPageAction } from '@/hooks/utils/useRegisterPageAction';
import { showWarningToast } from '@/plugins/Toast';
import { getCompanyAttributeList } from '@/utils/company';
import { validationMessageKey } from '@/utils/validationMessage';

interface SelectRevieweeLocationState {
    preselectedEmployeeId?: string;
}

export const SelectRevieweePage = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const { cycleInstanceId } = useParams<Evaluation360FormNavigatorParams>();
    const location = useLocation<SelectRevieweeLocationState>();
    const preselectedEmployeeId = location.state?.preselectedEmployeeId;
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const pageSize = 20;

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const { data: companyInformation } = useGetCompanyInformation();

    const {
        data: cycleInstance,
        isLoading: isCycleInstanceLoading,
        isError: isCycleInstanceError,
    } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });

    const {
        selectedCompanyAttributes,
        filters,
        onSelectCompanyAttribute,
        onSelectCompanyAttributeOption,
    } = useCompanyAttributesFilters({
        company: companyInformation,
        storageKey: Evaluation360Storage.SELECT_REVIEWEE,
    });

    const filterParams: UserFilterParams = useMemo(() => {
        const toIds = (items: { id?: string }[]) =>
            items.map((i) => i.id).filter((id): id is string => !!id);

        const params: UserFilterParams = {};
        if (filters.departments.length > 0) params.departmentIds = toIds(filters.departments);
        if (filters.jobTitles.length > 0) params.jobTitleIds = toIds(filters.jobTitles);
        if (filters.managers.length > 0) params.managerIds = toIds(filters.managers);
        if (filters.companyAddresses.length > 0)
            params.companyAddressIds = toIds(filters.companyAddresses);
        if (filters.employeeLevels.length > 0)
            params.employeeLevelIds = toIds(filters.employeeLevels);
        if (filters.customAttributes.length > 0)
            params.customAttributeIds = toIds(filters.customAttributes);
        return params;
    }, [filters]);

    const { mutateAsync: updateReviewAudience } = useUpdateReviewAudience();

    const { watch, setValue, trigger, getFieldState } = useForm({
        resolver: zodResolver(selectRevieweeSchema),
        defaultValues: {
            [SelectRevieweeFormFields.selectedReviewees]: [],
        },
    });
    const selectedReviewees = watch(SelectRevieweeFormFields.selectedReviewees);

    const {
        data: permissions,
        isLoading: permissionsLoading,
        isError: permissionsError,
    } = useGetUserPermissions();

    const {
        data: users,
        isLoading: companyPeopleLoading,
        isError: companyPeopleError,
    } = useGetCompanyPeople(
        {
            pageNumber: currentPage,
            pageSize,
            searchQuery:
                debouncedSearchTerm.trim().length >= 2 ? debouncedSearchTerm.trim() : undefined,
            isActiveOnly: true,
            includeInvited: true,
            ...filterParams,
        },
        {
            canViewResultsOfAllDepartments: permissions?.canViewResultsOfAllDepartments,
        },
    );

    const reviewAudience = useMemo(() => {
        return cycleInstance?.reviewAudience;
    }, [cycleInstance]);

    const preselectedRevieweeIds = useMemo(() => {
        const assignments = reviewAudience?.reviewAudienceAssignments || [];

        return [...new Set(assignments.map((assignment) => assignment.assignedTo))];
    }, [reviewAudience?.reviewAudienceAssignments]);

    const {
        data: preselectedRevieweeUsers,
        isLoading: preselectedRevieweeUsersLoading,
        isError: preselectedRevieweeUsersError,
    } = useGetUsersByIds({
        userIds: preselectedRevieweeIds,
        identifier: `select-reviewee-${cycleInstanceId}`,
    });

    // Fetch preselected employee from location state (when navigating from performance profile)
    const { data: preselectedEmployeeUser, isLoading: preselectedEmployeeUserLoading } =
        useGetUserById({
            userId: preselectedEmployeeId ?? '',
        });

    const isLoading =
        permissionsLoading ||
        companyPeopleLoading ||
        isCycleInstanceLoading ||
        preselectedRevieweeUsersLoading ||
        (preselectedEmployeeId ? preselectedEmployeeUserLoading : false);

    const reviewees = useMemo(() => users?.items || [], [users]);

    const hasInitializedRef = useRef(false);

    useEffect(() => {
        if (hasInitializedRef.current || !reviewAudience) {
            return;
        }

        if (preselectedRevieweeIds.length === 0 || preselectedRevieweeUsersError) {
            hasInitializedRef.current = true;

            return;
        }

        if (!preselectedRevieweeUsers) {
            return;
        }

        setValue(SelectRevieweeFormFields.selectedReviewees, preselectedRevieweeUsers);

        hasInitializedRef.current = true;
    }, [
        reviewAudience,
        preselectedRevieweeIds.length,
        preselectedRevieweeUsers,
        preselectedRevieweeUsersError,
        setValue,
    ]);

    // Handle preselected employee from location state (when navigating from performance profile)
    const hasInitializedFromLocationStateRef = useRef(false);

    useEffect(() => {
        if (hasInitializedFromLocationStateRef.current) {
            return;
        }

        if (!preselectedEmployeeId || !preselectedEmployeeUser) {
            return;
        }

        // Only auto-select if no reviewees are already selected
        if (selectedReviewees.length === 0) {
            setValue(SelectRevieweeFormFields.selectedReviewees, [preselectedEmployeeUser]);
        }

        hasInitializedFromLocationStateRef.current = true;
    }, [preselectedEmployeeId, preselectedEmployeeUser, selectedReviewees.length, setValue]);

    const companyAttributes: OptionProps[] = useMemo(() => {
        const attributes = getCompanyAttributeList(companyInformation);

        // Age groups and tenures are excluded as they are not relevant for reviewee selection
        const excludedAttributes: string[] = ['ageGroups', 'tenures'];

        return Object.entries(attributes)
            .filter(([key]) => !excludedAttributes.includes(key))
            .map(([, attr]) => ({
                label: attr.name,
                value: attr.name,
            }));
    }, [companyInformation]);

    const onSelectReviewee = (user: User) => {
        const isAlreadySelected = selectedReviewees.some((reviewee) => reviewee.id === user.id);

        if (isAlreadySelected) {
            setValue(
                SelectRevieweeFormFields.selectedReviewees,
                selectedReviewees.filter((reviewee) => reviewee.id !== user.id),
            );
        } else {
            setValue(SelectRevieweeFormFields.selectedReviewees, [...selectedReviewees, user]);
        }
    };

    // Pagination handlers
    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const paginationData = useMemo(
        () =>
            users
                ? {
                      currentPage,
                      totalPages: Math.ceil(users.totalCount / users.pageSize),
                      totalCount: users.totalCount,
                      pageSize,
                      hasNextPage: users.hasNextPage,
                      onPageChange: handlePageChange,
                  }
                : undefined,
        [users, currentPage, pageSize, handlePageChange],
    );

    // Reset to first page when filters change or debounced search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filterParams, debouncedSearchTerm]);

    const onNext = useCallback(async () => {
        const isValid = await trigger(SelectRevieweeFormFields.selectedReviewees);

        if (!isValid) {
            const errorMessage = validationMessageKey(
                getFieldState(SelectRevieweeFormFields.selectedReviewees).error?.message,
            );

            showWarningToast({
                title: t('evaluation360SelectReviewee.validation.title'),
                message: t(errorMessage ?? 'evaluation360SelectReviewee.validation.message'),
            });

            return false;
        }

        if (!reviewAudience) {
            return false;
        }

        const updatedAssignments = selectedReviewees.map((reviewee) => {
            const existingAssignment = reviewAudience.reviewAudienceAssignments.find(
                (assignment) => assignment.assignedTo === reviewee.id,
            );

            if (existingAssignment) {
                return existingAssignment;
            }

            return {
                id: '',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                reviewAudienceId: reviewAudience.id,
                reviewAudience: reviewAudience,
                reviewEvaluatorAssignments: [],
                assignedTo: reviewee.id,
            };
        });

        try {
            await updateReviewAudience({
                ...reviewAudience,
                reviewAudienceAssignments: updatedAssignments,
            });

            return true;
        } catch (error) {
            handleError({ error });

            return false;
        }
    }, [
        trigger,
        getFieldState,
        handleError,
        updateReviewAudience,
        reviewAudience,
        selectedReviewees,
        t,
    ]);

    const isSelfEvaluation = cycleInstance?.reviewType === EvaluationType.SELF_EVALUATION;

    useRegisterPageAction(selectRevieweeOnNextAtom, onNext);

    useFetchErrorsHandler({
        errors: [
            isCycleInstanceError,
            permissionsError,
            companyPeopleError,
            preselectedRevieweeUsersError,
        ],
    });

    return (
        <SelectReviewee
            isSelfEvaluation={isSelfEvaluation}
            companyAttributes={companyAttributes}
            selectedCompanyAttributes={selectedCompanyAttributes}
            filters={filters}
            reviewees={reviewees}
            selectedReviewees={selectedReviewees}
            selectReviewee={onSelectReviewee}
            onSelectCompanyAttribute={onSelectCompanyAttribute}
            onSelectCompanyAttributeOption={onSelectCompanyAttributeOption}
            isLoading={isLoading || isCycleInstanceLoading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            paginationData={paginationData}
        />
    );
};
